// Utility for handling embedded image markers stored inside clinical text fields.
//
// Images are saved inline within text fields (diagnosis, notes, lab results, etc.)
// using the marker format:  [IMAGE:data:image/<type>;base64,<data>]
// Usually preceded by a label on the same line, e.g.
//   "Ultrasound: [IMAGE:data:image/jpeg;base64,/9j/...]"   (consultation notes)
//   "CBC = [IMAGE:data:image/png;base64,iVBOR...]"          (lab results)
//
// These helpers separate the raw image data from the readable text so that the
// Base64 string is NEVER shown to patients, while the readable clinical text
// (diagnosis, notes, ultrasound findings, results) is always preserved and the
// images are rendered/embedded.
//
// Robustness notes:
//  - Image Base64 payloads can be very large. We must handle a marker that has
//    been truncated and is therefore missing its closing "]" — otherwise the raw
//    payload would leak into the rendered text.
//  - A field whose only content is a marker must still keep its label so the
//    surrounding section is never blanked out.

// A complete marker: "[IMAGE:" ... up to the first closing "]".
// Base64 never contains "]", so a "anything but ]" capture is safe and linear.
const COMPLETE_MARKER_REGEX = /\[IMAGE:\s*([^\]]+?)\s*\]/gi;

// A complete marker together with any immediately preceding "label:" / "label ="
// separator, so the dangling separator can be removed from the readable text.
const COMPLETE_MARKER_WITH_SEPARATOR_REGEX = /[ \t]*[:=]?[ \t]*\[IMAGE:\s*[^\]]+?\s*\]/gi;

// A truncated marker left at the end of the text with no closing "]".
const UNTERMINATED_MARKER_REGEX = /[ \t]*[:=]?[ \t]*\[IMAGE:\s*([^\]]*)$/i;

/**
 * Returns true if the given text contains any [IMAGE: marker (complete or not).
 */
export function hasImageMarker(text) {
    return typeof text === 'string' && /\[IMAGE:/i.test(text);
}

/**
 * Derive a best-effort label from the text immediately preceding a marker on the
 * same line (e.g. "Ultrasound", "CBC"), with a trailing ":" or "=" removed.
 */
function labelFromPrefix(beforeText, fallbackIndex) {
    const lineStart = beforeText.lastIndexOf('\n') + 1;
    const prefix = beforeText
        .slice(lineStart)
        .trim()
        .replace(/[:=]\s*$/, '')
        .trim();
    return prefix || `Image ${fallbackIndex}`;
}

/**
 * Parse a text field, separating embedded images from readable text.
 *
 * @param {string} text
 * @returns {{ cleanText: string, images: Array<{ label: string, dataUrl: string }> }}
 *   cleanText - the original text with every image marker (and any dangling
 *               "label:" separator) removed, with blank lines collapsed.
 *   images    - the extracted images, each with a best-effort label and the raw
 *               data URL ready to be rendered or embedded. Truncated/corrupt
 *               payloads are still returned so the consumer can show a graceful
 *               fallback instead of leaking the raw Base64.
 */
export function extractImages(text) {
    if (!text || typeof text !== 'string') {
        return { cleanText: typeof text === 'string' ? text : '', images: [] };
    }

    const images = [];

    // 1. Extract every complete marker.
    let match;
    COMPLETE_MARKER_REGEX.lastIndex = 0;
    while ((match = COMPLETE_MARKER_REGEX.exec(text)) !== null) {
        const dataUrl = match[1].trim();
        if (!dataUrl) continue;
        images.push({ label: labelFromPrefix(text.slice(0, match.index), images.length + 1), dataUrl });
    }

    // 2. Remove all complete markers (and any preceding separator) from the text.
    let cleanText = text.replace(COMPLETE_MARKER_WITH_SEPARATOR_REGEX, '');

    // 3. Handle a truncated marker left at the very end (no closing "]"). This is
    //    what happens with oversized payloads and is the main way raw Base64 used
    //    to leak into the UI. Capture it as a (likely unrenderable) image and
    //    strip it from the text so nothing leaks.
    const trailing = cleanText.match(UNTERMINATED_MARKER_REGEX);
    if (trailing) {
        const dataUrl = (trailing[1] || '').trim();
        if (dataUrl) {
            images.push({ label: labelFromPrefix(cleanText.slice(0, trailing.index), images.length + 1), dataUrl });
        }
        cleanText = cleanText.slice(0, trailing.index);
    }

    // 4. Tidy up: drop trailing separators/whitespace and collapse the blank
    //    lines that removing a marker may have produced, without dropping any
    //    readable content.
    cleanText = cleanText
        .split('\n')
        .map((line) => line.replace(/[ \t]*[:=][ \t]*$/, '').replace(/[ \t]+$/, ''))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    return { cleanText, images };
}

/**
 * Validate that a string looks like a renderable image data URL.
 * Used to provide graceful fallbacks for corrupted/unsupported/truncated data.
 */
export function isRenderableImage(dataUrl) {
    return typeof dataUrl === 'string' && /^data:image\/[a-zA-Z0-9.+-]+;base64,/i.test(dataUrl.trim());
}

/**
 * Escape a value for safe interpolation into HTML built with document.write.
 */
export function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Build a safe HTML fragment for a clinical text field, for the printed reports
 * that are assembled as HTML strings and rendered via document.write.
 *
 * The readable text is HTML-escaped (newlines become <br>) and every embedded
 * image is rendered as an actual <img>. Raw Base64 markers are never emitted.
 * Truncated/corrupt images degrade to a short "could not be displayed" note.
 *
 * @param {string} text                The raw field value (may contain markers).
 * @param {object} [options]
 * @param {string} [options.imageStyle] Inline style applied to each <img>.
 * @param {string} [options.emptyText]  Placeholder when the field has no
 *                                       readable text and no images (e.g. "N/A").
 * @returns {string} An HTML fragment safe to inject into a report document.
 */
export function renderFieldHtml(text, options = {}) {
    const {
        imageStyle = 'max-width:100%;max-height:320px;height:auto;border-radius:6px;border:1px solid #ddd;display:block;margin:8px 0;',
        emptyText = ''
    } = options;

    const { cleanText, images } = extractImages(text);

    let html = '';
    if (cleanText) {
        html += escapeHtml(cleanText).replace(/\n/g, '<br>');
    }

    images.forEach((img) => {
        const label = escapeHtml(img.label);
        if (isRenderableImage(img.dataUrl)) {
            html += `<div style="margin-top:8px;">` +
                `<div style="font-weight:600;margin-bottom:4px;">${label}</div>` +
                `<img src="${img.dataUrl}" alt="${label}" style="${imageStyle}" />` +
                `</div>`;
        } else {
            html += `<div style="margin-top:8px;color:#9a3b3b;font-style:italic;">[${label}: image could not be displayed]</div>`;
        }
    });

    if (!html && emptyText) {
        html = escapeHtml(emptyText);
    }

    return html;
}

/**
 * Embed a list of extracted images into a jsPDF document, mirroring the behaviour
 * used by the public patient report. Returns the new vertical cursor position.
 * Corrupt/unsupported images degrade to a short note instead of failing.
 *
 * @param {jsPDF} doc
 * @param {Array<{ label: string, dataUrl: string }>} images
 * @param {number} startY                  Vertical cursor to start drawing at.
 * @param {object} [layout]
 * @param {number} [layout.marginX]        Left margin for the label/image.
 * @param {number} [layout.maxWidth]       Maximum image width.
 * @param {number} [layout.maxHeight]      Maximum image height.
 * @param {number} [layout.bottomMargin]   Reserved space at the bottom of a page.
 * @returns {number} The vertical cursor position after the images.
 */
export function embedImagesInPdf(doc, images, startY, layout = {}) {
    const pageHeight = doc.internal.pageSize.getHeight();
    const {
        marginX = 14,
        maxWidth = 180,
        maxHeight = 110,
        bottomMargin = 20
    } = layout;

    let yPos = startY;

    const ensureSpace = (needed) => {
        if (yPos + needed > pageHeight - bottomMargin) {
            doc.addPage();
            yPos = 20;
        }
    };

    (images || []).forEach((img) => {
        try {
            const props = doc.getImageProperties(img.dataUrl);
            if (!props || !props.width || !props.height) {
                throw new Error('Invalid image properties');
            }

            let drawWidth = maxWidth;
            let drawHeight = (props.height / props.width) * drawWidth;
            if (drawHeight > maxHeight) {
                drawHeight = maxHeight;
                drawWidth = (props.width / props.height) * drawHeight;
            }

            ensureSpace(drawHeight + 8);
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(44, 62, 80);
            doc.text(img.label, marginX, yPos);
            yPos += 4;
            doc.setFont(undefined, 'normal');
            doc.addImage(img.dataUrl, props.fileType || 'JPEG', marginX, yPos, drawWidth, drawHeight);
            yPos += drawHeight + 6;
        } catch (imgErr) {
            // Graceful fallback for corrupted / unsupported / truncated images.
            console.error('Could not embed image in PDF:', imgErr);
            ensureSpace(8);
            doc.setFontSize(9);
            doc.setFont(undefined, 'italic');
            doc.setTextColor(150, 0, 0);
            doc.text(`[${img.label}: image could not be displayed]`, marginX, yPos);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
            yPos += 6;
        }
    });

    return yPos;
}
